"use client";

import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "@/lib/firebase";

const STORAGE_KEY = "marginglow_last_worked_field";
const EDITABLE_SELECTOR = [
  'input:not([type="password"]):not([type="checkbox"]):not([type="radio"]):not([type="hidden"]):not([type="file"])',
  "textarea",
  "select",
  '[contenteditable="true"]',
].join(", ");

type EditableElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement
  | HTMLElement;

function isTrackable(element: EditableElement) {
  if (
    element.closest('[data-auth-form="true"]') ||
    element.closest('[data-marginglow-ignore-work="true"]')
  ) {
    return false;
  }

  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement
  ) {
    return !element.disabled && !element.readOnly;
  }

  if (element instanceof HTMLSelectElement) {
    return !element.disabled;
  }

  return element.getAttribute("contenteditable") === "true";
}

function getFieldKey(element: EditableElement) {
  if (!isTrackable(element)) {
    return null;
  }

  const identity =
    element.getAttribute("data-work-key") ||
    element.id ||
    element.getAttribute("name") ||
    element.getAttribute("aria-label") ||
    element.getAttribute("placeholder");

  if (!identity) {
    return null;
  }

  const area = element.closest<HTMLElement>(
    "section[id], form[id], [data-work-area]",
  );

  const areaKey =
    area?.id ||
    area?.getAttribute("data-work-area") ||
    "page";

  return [
    window.location.pathname,
    areaKey,
    element.tagName.toLowerCase(),
    identity.trim().slice(0, 160),
  ].join("|");
}

function getFieldLabel(element: EditableElement) {
  const wrappedLabel =
    element.closest<HTMLLabelElement>("label");

  if (wrappedLabel?.innerText.trim()) {
    return wrappedLabel.innerText.trim().slice(0, 90);
  }

  if (element.id) {
    const externalLabel = Array.from(
      document.querySelectorAll<HTMLLabelElement>("label"),
    ).find((label) => label.htmlFor === element.id);

    if (externalLabel?.innerText.trim()) {
      return externalLabel.innerText.trim().slice(0, 90);
    }
  }

  return (
    element.getAttribute("aria-label") ||
    element.getAttribute("placeholder") ||
    "your most recent work"
  ).slice(0, 90);
}

export default function WorkContinuity() {
  const [lastLabel, setLastLabel] = useState("");
  const [targetAvailable, setTargetAvailable] =
    useState(false);

  useEffect(() => {
    let activeKey =
      window.localStorage.getItem(STORAGE_KEY) || "";
    let saveTimer: number | undefined;

    function fields() {
      return Array.from(
        document.querySelectorAll<EditableElement>(
          EDITABLE_SELECTOR,
        ),
      ).filter(isTrackable);
    }

    function clearMarker() {
      document
        .querySelectorAll(".marginglow-last-worked-field")
        .forEach((element) => {
          element.classList.remove(
            "marginglow-last-worked-field",
          );
          element.removeAttribute(
            "data-marginglow-last-worked",
          );
        });
    }

    function findTarget() {
      if (!activeKey) {
        return null;
      }

      return (
        fields().find(
          (element) => getFieldKey(element) === activeKey,
        ) || null
      );
    }

    function applyMarker() {
      clearMarker();
      const target = findTarget();

      if (!target) {
        setTargetAvailable(false);
        return;
      }

      target.classList.add(
        "marginglow-last-worked-field",
      );
      target.setAttribute(
        "data-marginglow-last-worked",
        "true",
      );
      setLastLabel(getFieldLabel(target));
      setTargetAvailable(true);
    }

    function recordWork(event: Event) {
      const target = event.target;

      if (
        !(
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          target instanceof HTMLElement
        )
      ) {
        return;
      }

      if (!target.matches(EDITABLE_SELECTOR)) {
        return;
      }

      const key = getFieldKey(target);

      if (!key) {
        return;
      }

      activeKey = key;
      window.localStorage.setItem(STORAGE_KEY, key);
      applyMarker();

      const userId = auth.currentUser?.uid;

      if (!userId) {
        return;
      }

      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => {
        void setDoc(
          doc(db, "users", userId),
          {
            lastWorkedFieldKey: key,
            lastWorkedAt: serverTimestamp(),
          },
          { merge: true },
        ).catch((error) => {
          console.error(
            "Last-worked position could not be saved:",
            error,
          );
        });
      }, 700);
    }

    document.addEventListener("input", recordWork, true);
    document.addEventListener("change", recordWork, true);

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(applyMarker);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        if (user) {
          try {
            const snapshot = await getDoc(
              doc(db, "users", user.uid),
            );
            const remoteKey =
              snapshot.data()?.lastWorkedFieldKey;

            if (
              typeof remoteKey === "string" &&
              remoteKey
            ) {
              activeKey = remoteKey;
              window.localStorage.setItem(
                STORAGE_KEY,
                remoteKey,
              );
            }
          } catch (error) {
            console.error(
              "Last-worked position could not be restored:",
              error,
            );
          }
        }

        window.setTimeout(applyMarker, 250);
        window.setTimeout(applyMarker, 1000);
      },
    );

    applyMarker();

    return () => {
      document.removeEventListener(
        "input",
        recordWork,
        true,
      );
      document.removeEventListener(
        "change",
        recordWork,
        true,
      );
      observer.disconnect();
      unsubscribe();
      window.clearTimeout(saveTimer);
    };
  }, []);

  function continueWorking() {
    const key =
      window.localStorage.getItem(STORAGE_KEY);

    if (!key) {
      return;
    }

    const target = Array.from(
      document.querySelectorAll<EditableElement>(
        EDITABLE_SELECTOR,
      ),
    ).find(
      (element) =>
        isTrackable(element) &&
        getFieldKey(element) === key,
    );

    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    window.setTimeout(() => {
      target.focus({ preventScroll: true });
    }, 450);
  }

  if (!targetAvailable) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={continueWorking}
      className="fixed bottom-5 left-5 z-[70] max-w-[min(22rem,calc(100vw-2.5rem))] rounded-2xl border border-[#52aaa3] bg-white px-4 py-3 text-left shadow-[0_14px_35px_rgba(9,39,35,0.18)] transition hover:-translate-y-0.5 hover:bg-[#f4fbf8]"
      aria-label="Continue where you left off"
    >
      <span className="block text-xs font-normal uppercase tracking-[0.14em] text-[#007c7a]">
        Continue where you left off
      </span>
      <span className="mt-1 block truncate text-sm font-light text-[#52635f]">
        {lastLabel}
      </span>
    </button>
  );
}
